import React, { useEffect, useState } from 'react';
import { FiEye, FiMail, FiSend, FiX } from 'react-icons/fi';
import {
  ContactEmailDraft,
  ContactEmailItem,
  ContactEmailReplyInput,
  ContactEmailStatus,
  contactEmailErrorMessage,
  previewContactEmailReply,
  sendContactEmailReply,
} from '../../services/ContactEmailService';
import { isEmailDraftConflict, newEmailIdempotencyKey } from './emailDraft';
import './emailConversation.css';

/** O que foi pré-visualizado: o envio manda exatamente isto, com a impressão e a chave deste rascunho. */
interface PreviewedDraft {
  draft: ContactEmailDraft;
  input: ContactEmailReplyInput;
  idempotencyKey: string;
}

interface Props {
  sectorId: number;
  contactId: number;
  status: ContactEmailStatus | null;
  /** E-mail respondido (citado); null = o último e-mail do cliente, escolhido pelo servidor. */
  replyTo: ContactEmailItem | null;
  onClearReplyTo?: () => void;
  onSent: (item: ContactEmailItem, captured: boolean) => void;
  /** Texto inicial (ex.: sugestão da IA). */
  initialBody?: string;
}

/**
 * Resposta por e-mail dentro da conversa. Pré-visualizar mostra exatamente o que vai sair (remetente, destinatário,
 * assunto, texto e a citação do e-mail respondido); enviar manda esse mesmo rascunho (e-mail respondido + impressão da
 * prévia + chave de idempotência): se algo mudou no servidor, volta 409 e pede nova pré-visualização.
 */
export const EmailReplyComposer: React.FC<Props> = ({ sectorId, contactId, status, replyTo, onClearReplyTo, onSent, initialBody }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(initialBody ?? '');
  const [previewed, setPreviewed] = useState<PreviewedDraft | null>(null);
  const draft = previewed?.draft ?? null;
  const [busy, setBusy] = useState<'preview' | 'send' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    setSubject('');
    setBody(initialBody ?? '');
    setPreviewed(null);
    setError(null);
    setNotice(null);
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialBody) setBody(initialBody);
  }, [initialBody]);

  useEffect(() => {
    setPreviewed(null);
  }, [replyTo?.id]);

  if (status && !status.canReply) {
    return (
      <div className="email-composer email-composer-blocked" role="status">
        <FiMail aria-hidden="true" /> {status.reason || 'Não dá para responder por e-mail nesta conversa.'}
      </div>
    );
  }

  const input = (): ContactEmailReplyInput => ({ body, subject: subject.trim() || undefined, replyToEmailId: replyTo?.id ?? null });

  const preview = async () => {
    setError(null);
    setNotice(null);
    setBusy('preview');
    try {
      const asked = input();
      const result = await previewContactEmailReply(sectorId, contactId, asked);
      setPreviewed({ draft: result, input: asked, idempotencyKey: newEmailIdempotencyKey() });
    } catch (e) {
      setError(contactEmailErrorMessage(e, 'Não foi possível montar a pré-visualização.'));
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    if (!previewed) return;
    setError(null);
    setBusy('send');
    try {
      const result = await sendContactEmailReply(sectorId, contactId, {
        ...previewed.input,
        // O e-mail respondido que o servidor escolheu na prévia (mesmo quando o usuário não escolheu nenhum).
        replyToEmailId: previewed.draft.replyToEmailId,
        draftFingerprint: previewed.draft.fingerprint,
        idempotencyKey: previewed.idempotencyKey,
      });
      onSent(result.item, result.captured);
      setPreviewed(null);
      setBody('');
      setSubject('');
      onClearReplyTo?.();
      setNotice(result.captured
        ? 'Modo local: o e-mail foi capturado em arquivo, nada saiu da máquina.'
        : 'E-mail enviado.');
    } catch (e) {
      if (isEmailDraftConflict(e)) {
        // O e-mail que sairia agora não é o da prévia (chegou e-mail novo, mudou a caixa…): volta para editar e pré-visualizar de novo.
        setPreviewed(null);
        setError(contactEmailErrorMessage(e, 'O e-mail mudou desde a pré-visualização. Pré-visualize de novo antes de enviar.'));
      } else {
        setError(contactEmailErrorMessage(e, 'Não foi possível enviar o e-mail agora.'));
      }
    } finally {
      setBusy(null);
    }
  };

  if (draft) {
    return (
      <div className="email-composer email-preview" aria-label="Pré-visualização do e-mail">
        <div className="email-preview-title"><FiEye aria-hidden="true" /> Pré-visualização — confira antes de enviar</div>
        <dl className="email-preview-meta">
          <dt>De</dt><dd>{draft.from || '—'}</dd>
          <dt>Para</dt><dd>{draft.to}</dd>
          <dt>Assunto</dt><dd>{draft.subject}</dd>
        </dl>
        <div className="email-preview-body">{draft.bodyText}</div>
        {draft.quotedText && (
          <div className="email-preview-quote">
            <div className="email-preview-quote-label">Citação do e-mail respondido</div>
            {draft.quotedText}
          </div>
        )}
        {error && <div className="email-composer-error" role="alert">{error}</div>}
        <div className="email-composer-actions">
          <button type="button" className="email-btn email-btn-ghost" onClick={() => setPreviewed(null)} disabled={busy === 'send'}>
            Voltar e editar
          </button>
          <button type="button" className="email-btn email-btn-primary" onClick={() => void send()} disabled={busy === 'send'}>
            <FiSend aria-hidden="true" /> {busy === 'send' ? 'Enviando…' : 'Enviar e-mail'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="email-composer">
      <div className="email-composer-row">
        <span className="email-composer-to">
          <FiMail aria-hidden="true" /> Para: <strong>{status?.contactEmail || 'e-mail do cliente'}</strong>
          {status?.mailbox && <span className="email-composer-from"> · de {status.mailbox}</span>}
        </span>
        {replyTo && (
          <span className="email-composer-replying">
            Respondendo: {replyTo.subject || '(sem assunto)'}
            {onClearReplyTo && (
              <button type="button" className="email-icon-btn" aria-label="Não responder a este e-mail" onClick={onClearReplyTo}>
                <FiX size={12} />
              </button>
            )}
          </span>
        )}
      </div>
      <input
        className="email-composer-subject"
        placeholder={replyTo || status?.contactEmail ? 'Assunto (vazio = "Re:" do último e-mail)' : 'Assunto'}
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        maxLength={300}
        aria-label="Assunto do e-mail"
      />
      <textarea
        className="email-composer-body"
        placeholder="Escreva a resposta por e-mail…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        aria-label="Texto do e-mail"
        rows={4}
      />
      {status?.localSafeMode && (
        <div className="email-composer-hint">Modo local seguro: o envio é só gravado em arquivo, nada sai da máquina.</div>
      )}
      {error && <div className="email-composer-error" role="alert">{error}</div>}
      {notice && <div className="email-composer-notice" role="status">{notice}</div>}
      <div className="email-composer-actions">
        <button
          type="button"
          className="email-btn email-btn-primary"
          onClick={() => void preview()}
          disabled={!body.trim() || busy !== null}
        >
          <FiEye aria-hidden="true" /> {busy === 'preview' ? 'Montando…' : 'Pré-visualizar'}
        </button>
      </div>
    </div>
  );
};
